import { useMemo, useState } from "react";
import { toast } from "sonner";
import { dealerApi } from "../utils/dealerApi";

/**
 * Encapsulates the dealer "Sellers" / "Back-Office" management area:
 * state, fetcher and CRUD handlers.
 *
 * Zorzutti CR 12 Feb 2026 — the previous single-checkbox UX (a "Utenza
 * Back-Office" toggle inside the Sellers form) was replaced by two
 * separate top-level menu items: "Venditori" and "Back-Office". A
 * Back-Office employee is NOT a type of Seller, so the data is the same
 * on the wire but the UI shows two distinct lists/forms. The hook now
 * accepts a `mode` parameter that:
 *   • filters the fetched list to entries matching `is_back_office`
 *     (`mode === 'back_office'` ⇒ true, else ⇒ false)
 *   • forces the same `is_back_office` flag on the create payload,
 *     so the checkbox can be removed entirely from the UI
 *
 * @param dealerToken  current JWT (sent via ``Authorization: Bearer`` header)
 * @param t            translations object from `getDealerTranslation(lang)`
 * @param mode         `'seller'` (default) | `'back_office'`
 */
export const useDealerSellers = ({ dealerToken, t, mode = "seller" }) => {
  const isBackOffice = mode === "back_office";

  const [allRecords, setAllRecords] = useState([]);
  const [sellerForm, setSellerForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", department: "",
  });
  const [creatingSeller, setCreatingSeller] = useState(false);
  const [showSellerForm, setShowSellerForm] = useState(false);

  // Filtered view: Venditori page hides BO users, BO page hides Venditori.
  const sellers = useMemo(
    () => allRecords.filter(s => !!s.is_back_office === isBackOffice),
    [allRecords, isBackOffice],
  );

  const fetchSellers = async (tk) => {
    try {
      const res = await dealerApi.get(`/dealer/sellers`, { token: tk || dealerToken });
      setAllRecords(res.data);
    } catch { /* silent — mirrors original */ }
  };

  const handleCreateSeller = async (e) => {
    e.preventDefault();
    setCreatingSeller(true);
    try {
      // The role is fixed by the page the user is on — no checkbox.
      await dealerApi.post(`/dealer/sellers`, { token: dealerToken }, {
        ...sellerForm,
        is_back_office: isBackOffice,
      });
      toast.success(isBackOffice
        ? (t.backOfficeCreated || t.sellerCreated)
        : t.sellerCreated);
      setSellerForm({ first_name: "", last_name: "", email: "", phone: "", department: "" });
      setShowSellerForm(false);
      fetchSellers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.sellerCreateError);
    } finally {
      setCreatingSeller(false);
    }
  };

  const handleToggleSeller = async (sellerId) => {
    try {
      const res = await dealerApi.put(`/dealer/sellers/${sellerId}/toggle`, { token: dealerToken });
      toast.success(res.data.is_active ? t.sellerActivated : t.sellerDeactivated);
      fetchSellers();
    } catch { toast.error(t.toggleError); }
  };

  const handleDeleteSeller = async (sellerId, sellerName) => {
    if (!window.confirm(`${t.deleteSeller} ${sellerName}? ${t.deleteConfirm}`)) return;
    try {
      await dealerApi.delete(`/dealer/sellers/${sellerId}`, { token: dealerToken });
      toast.success(t.sellerDeleted);
      fetchSellers();
    } catch { toast.error(t.deleteError); }
  };

  const handleResendSetup = async (sellerId) => {
    try {
      await dealerApi.post(`/dealer/sellers/resend-setup/${sellerId}`, { token: dealerToken });
      toast.success(t.activationResent);
    } catch { toast.error(t.sendError); }
  };

  return {
    sellers,
    sellerForm,
    setSellerForm,
    creatingSeller,
    showSellerForm,
    setShowSellerForm,
    fetchSellers,
    handleCreateSeller,
    handleToggleSeller,
    handleDeleteSeller,
    handleResendSetup,
  };
};
